/* CWR 1.2 — ClientShell chrome. Fills the Mini App viewport: fixed header at
   top, scrollable <main> below, floating nav over the bottom. Themed via the
   injected client tokens (active under :root[data-client-theme]). */

.layout {
  --lockup-strip-row: max(var(--tg-content-safe-area-inset-top, 0px), 44px);
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  background: var(--bg-page, #050403);
  color: var(--text-primary, #faf3e8);
  overflow: hidden;
}

.atmosphere {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  /* Keep the image's cover geometry locked to the pre-keyboard viewport.
     The shell may shrink for form interaction, but this decorative layer is
     only clipped — never re-scaled/re-centered — while the keyboard animates. */
  height: var(--app-viewport-stable-height, 100dvh);
  z-index: 0;
  /* Documented composition (presets.ts): overlay darkening, then the atmosphere
     image layer (the token carries its own center / cover no-repeat), over the
     page color. `none` fallbacks keep it valid before the theme is injected. */
  background:
    var(--page-overlay, none),
    var(--page-image, none),
    var(--bg-page, #050403);
  pointer-events: none;
}

.header {
  position: relative;
  z-index: 2;
  flex-shrink: 0;
  /* Clears the Telegram chrome in fullscreen: device notch + Telegram's own
     header inset (set by useTelegramSafeAreaCssVars; falls back to env()/0). */
  padding: calc(
      var(--tg-safe-area-inset-top, env(safe-area-inset-top, 0px))
      + var(--tg-content-safe-area-inset-top, 0px)
      + 10px
    )
    18px 12px;
}

.tabTitle {
  margin: 0;
  font-family: var(--client-font-heading, 'EB Garamond', Georgia, serif);
  font-weight: 500;
  font-size: 22px;
  letter-spacing: 0;
  text-align: center;
  color: var(--text-primary, #faf3e8);
}

.headerSkeleton {
  height: 56px;
  border-radius: 14px;
  background: var(--glass-bg, rgba(22, 20, 19, 0.6));
  opacity: 0.5;
}

.content {
  position: relative;
  z-index: 1;
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  /* Clearance for the floating nav (52px FAB + padding + safe area). */
  padding-bottom: calc(var(--tg-safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 110px);
}

.content::-webkit-scrollbar {
  display: none;
}

/* ── CWR 1.3 — Home-scoped header treatment (§3-A) ─────────────────────
   The greeting header is a transparent overlay-look and body content clips at
   the header's bottom edge (the flex <main> scroll box already begins there —
   no behind-header bleed, matching the mockup). Gated on isHome so the
   not-yet-designed placeholder tabs keep the centered-title band. */
.homeHeader {
  background: transparent;
  padding-bottom: 8px;
}

.homeContent {
  padding-top: 0;
}

/* Home LOGO mode with no logo: the wordmark starts right under Telegram's chrome
   row, with no band left over for a logo (Gabriel 2026-10-15). */
.wordmarkHeader {
  padding-top: calc(
    var(--tg-safe-area-inset-top, env(safe-area-inset-top, 0px))
    + var(--tg-content-safe-area-inset-top, 0px)
    + 2px
  );
}

/* Home LOGO mode: the content scrolls beneath a measured, non-interactive lockup.
   <main> itself carries NO clip-path or mask: either one makes it a backdrop root,
   which blinds the cards' backdrop-filter to the atmosphere behind it, and
   clip-path also drops the band from hit testing. The clip line is a cover
   element inside the header (ClientLogoLockupHeader) painting the atmosphere
   over the content band, so per-frame work is one clip-path on a static layer.
   --lockup-height: where the content starts, from the chrome-row top (102px, or 116px
   with the descriptor line), set inline by ClientShell. It must clear the cover's
   measured band (about 92px, or 106px with the descriptor) plus a gap, or the
   cover clips the first card's top edge at rest. */
.lockupHeader {
  position: absolute;
  inset: 0;
  padding: 0;
  pointer-events: none;
}

.lockupContent {
  margin-top: calc(var(--tg-safe-area-inset-top, env(safe-area-inset-top, 0px)) + var(--lockup-strip-row));
  padding-top: calc(var(--lockup-height, 102px) - var(--lockup-strip-row));
}

/* ── CWR 2.2 — page-owned header slot (e.g. the catalog's back + title + tabs).
   Keep the safe-area top clearance from .header; drop the default horizontal +
   bottom padding so the slot controls its own internal spacing (per the mockup,
   whose .shell-header has 0 horizontal padding and inner sections space it). */
.headerWithSlot {
  padding-left: 0;
  padding-right: 0;
  padding-bottom: 0;
}

.contentSkeleton {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
}

.spinner {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 3px solid var(--border-soft);
  border-top-color: var(--accent, #e8954f);
  animation: client-shell-spin 0.8s linear infinite;
}

@keyframes client-shell-spin {
  to {
    transform: rotate(360deg);
  }
}

/* ── Terminal takeover (error), no nav ───────────────────────────────────── */
.takeover {
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px;
  text-align: center;
  background: var(--bg-page, #050403);
  color: var(--text-secondary);
}

.takeoverTitle {
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  color: var(--text-primary, #faf3e8);
}

.takeoverMessage {
  margin: 0;
  max-width: 300px;
  font-size: 14px;
}
